import { useNavigate } from 'react-router-dom';
import { TopNav } from '@nx/ui';
import { useI18n } from '@nx/i18n';

export function MarketingHome() {
  const navigate = useNavigate();
  const { t } = useI18n();

  return (
    <div className="min-h-screen bg-white text-[#3D3328]">
      <TopNav onLogin={() => navigate('/login')} />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0b1020] via-[#151b30] to-[#10141f] text-blue-100">
        <div className="absolute inset-0 opacity-40 bg-[radial-gradient(circle_at_top,_rgba(56,189,248,0.35),_transparent_55%),radial-gradient(circle_at_bottom,_rgba(59,130,246,0.35),_transparent_55%)]" />
        <div className="max-w-6xl mx-auto px-4 md:px-8 py-16 md:py-24 relative z-10">
          <p className="text-sm md:text-base tracking-[0.2em] uppercase text-blue-300/80 mb-4">
            {t('marketing.eyebrow')}
          </p>
          <h1
            className="text-3xl md:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-blue-100"
            style={{ fontFamily: 'var(--font-sans)' }}
          >
            {t('marketing.heroTitle')}
          </h1>
          <p
            className="mt-4 md:mt-6 text-base md:text-xl max-w-2xl text-blue-100/80"
            style={{ fontFamily: 'var(--font-body)' }}
          >
            {t('marketing.heroSub')}
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <button
              onClick={() => navigate('/signup')}
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-[#1A1A1A] text-[#F4D03F] text-sm md:text-base shadow-lg shadow-black/40 hover:bg-black hover:shadow-xl transition-all font-bold"
            >
              {t('marketing.requestProposal')}
            </button>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-blue-300/40 text-blue-100/90 text-sm md:text-base hover:bg-blue-100/5 transition-all font-semibold"
            >
              {t('marketing.exploreExperiences')}
            </button>
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="bg-white py-12 md:py-16 border-b border-[#3D3328]/10">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">
            {t('marketing.pillarsTitle')}
          </h2>
          <p className="mt-3 text-sm md:text-base text-[#78665A] max-w-2xl">
            {t('marketing.pillarsSub')}
          </p>

          <div className="mt-8 grid gap-6 md:grid-cols-3">
            <Pillar
              title={t('marketing.pillar.regenerationTitle')}
              desc={t('marketing.pillar.regenerationDesc')}
            />
            <Pillar
              title={t('marketing.pillar.sportTitle')}
              desc={t('marketing.pillar.sportDesc')}
            />
            <Pillar
              title={t('marketing.pillar.cultureTitle')}
              desc={t('marketing.pillar.cultureDesc')}
            />
          </div>
        </div>
      </section>

      {/* Trust */}
      <section className="bg-white py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-[#1A1A1A]">
            {t('marketing.trustTitle')}
          </h2>
          <p className="mt-3 text-sm md:text-base text-[#78665A] max-w-3xl">
            {t('marketing.trustSub')}
          </p>

          <div className="mt-8 grid gap-4 md:grid-cols-2">
            <Pillar
              title="Measurable wellbeing impact"
              desc="Structured before/after checkpoints help teams track meaningful wellbeing outcomes."
              compact
            />
            <Pillar
              title="Stress reduction outcomes"
              desc="Retreat formats combine active recovery, movement, and guided decompression practices."
              compact
            />
            <Pillar
              title="Team engagement improvement"
              desc="Shared, high-quality experiences strengthen collaboration and improve participation."
              compact
            />
            <Pillar
              title="ESG alignment"
              desc="Local partners and responsible formats support social impact and territory-focused value creation."
              compact
            />
          </div>
        </div>
      </section>
    </div>
  );
}

function Pillar({ title, desc, compact }: { title: string; desc: string; compact?: boolean }) {
  return (
    <article
      className={`rounded-2xl bg-[#FAF8F5] border border-[#3D3328]/10 ${
        compact ? 'p-5' : 'p-6'
      } flex flex-col`}
    >
      <h3 className="text-base md:text-lg font-semibold text-[#1A1A1A]">{title}</h3>
      <p className="mt-2 text-sm text-[#78665A]">{desc}</p>
    </article>
  );
}
